import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useStateCleanup } from '@hooks/useStateCleanup';
import { SelectedFilterItem } from '@constants/data-table';
import {
  getMoreLikeThisPipelineTemplates as getMoreLikeThisPipelineTemplatesAction,
  getPipelineTemplateByName as getPipelineTemplateByNameAction,
  getPipelineTemplates as getPipelineTemplatesAction,
  getPipelineTemplatesByCategory as getPipelineTemplatesByCategoryAction,
  getPipelineTemplatesFilterTags as getPipelineTemplatesFilterTagsAction,
  selectTemplatesCategory as selectTemplatesCategoryAction,
  selectTemplatesFilters as selectTemplatesFiltersAction,
  selectTemplatesSortValue as selectTemplatesSortValueAction,
  setSelectedPipelineTemplate as setSelectedPipelineTemplateAction,
  setTemplatesSearchValue as setTemplatesSearchValueAction,
} from '@redux/actions/pipelineTemplatesActions';
import {
  moreTemplatesLikeThisSelector,
  moreTemplatesLikeThisStatusSelector,
  pipelineTemplatesByCategorySelector,
  pipelineTemplatesFiltersValuesSelector,
  pipelineTemplatesSearchValueSelector,
  pipelineTemplatesSelector,
  pipelineTemplatesSortValueSelector,
  selectedCategorySelector,
  selectedPipelineTemplateFiltersSelector,
  selectedTemplateSelector,
} from '@redux/selectors/pipelineTemplatesSelectors';
import {
  IPipelineTemplate,
  IPipelineTemplateCategoryParams,
  IPipelineTemplateParams,
  ITag,
  PipelineTemplatesCategory,
  PipelineTemplatesType,
} from '@redux/types/types';
import {
  INDEXING_SIDEBAR_CATEGORIES,
  QUERY_SIDEBAR_CATEGORIES,
} from '../constants/pipeline-templates';

const FETCH_LIMIT_FOR_ALL_GROUPED_TEMPLATES = 3;

const usePipelineTemplates = () => {
  const dispatch = useAppDispatch();

  const { reset } = useStateCleanup();

  const pipelineTemplates = useAppSelector(pipelineTemplatesSelector);
  const templatesByCategory = useAppSelector(pipelineTemplatesByCategorySelector);
  const selectedCategory = useAppSelector(selectedCategorySelector);
  const selectedTemplate = useAppSelector(selectedTemplateSelector);
  const moreTemplatesLikeThis = useAppSelector(moreTemplatesLikeThisSelector);
  const moreTemplatesLikeThisStatus = useAppSelector(moreTemplatesLikeThisStatusSelector);
  const pipelineTemplatesSearchValue = useAppSelector(pipelineTemplatesSearchValueSelector);
  const selectedPipelineTemplateFilters = useAppSelector(selectedPipelineTemplateFiltersSelector);
  const pipelineTemplatesSortValue = useAppSelector(pipelineTemplatesSortValueSelector);
  const pipelineTemplatesFiltersValues = useAppSelector(pipelineTemplatesFiltersValuesSelector);

  const getPipelineTemplates = (params: IPipelineTemplateParams) =>
    dispatch(getPipelineTemplatesAction(params));

  const getPipelineTemplatesByCategory = (params: IPipelineTemplateCategoryParams) =>
    dispatch(getPipelineTemplatesByCategoryAction(params));

  const fetchAllGroupedTemplates = async (pipelineType: PipelineTemplatesType) => {
    const sharedParams = {
      pipelineType,
      limit: FETCH_LIMIT_FOR_ALL_GROUPED_TEMPLATES,
      searchValue: pipelineTemplatesSearchValue,
      filterValues: selectedPipelineTemplateFilters,
      sortValue: pipelineTemplatesSortValue,
    };

    const categories =
      pipelineType === PipelineTemplatesType.INDEX
        ? INDEXING_SIDEBAR_CATEGORIES
        : QUERY_SIDEBAR_CATEGORIES;

    await Promise.all(
      categories.map((category: PipelineTemplatesCategory) =>
        getPipelineTemplatesByCategory({ ...sharedParams, category }),
      ),
    );
  };

  const selectTemplatesCategory = (category?: PipelineTemplatesCategory) =>
    dispatch(selectTemplatesCategoryAction(category ?? null));

  const setSelectedPipelineTemplate = (template: IPipelineTemplate) =>
    dispatch(setSelectedPipelineTemplateAction(template));

  const getMoreLikeThisPipelineTemplates = (tags: ITag[]) =>
    dispatch(getMoreLikeThisPipelineTemplatesAction({ tags }));

  const setTemplatesSearchValue = (searchValue: string) =>
    dispatch(setTemplatesSearchValueAction(searchValue));

  const getPipelineTemplateByName = (templateName: string) =>
    dispatch(getPipelineTemplateByNameAction({ templateName }));

  const getPipelineTemplatesFilterTags = () => dispatch(getPipelineTemplatesFilterTagsAction());

  const selectTemplatesFilters = (filterKey: string, items: SelectedFilterItem[]) =>
    dispatch(selectTemplatesFiltersAction(filterKey, items));

  const selectTemplatesSortValue = (value: string) =>
    dispatch(selectTemplatesSortValueAction(value));

  // Reset handlers

  const resetSelectedTemplate = () => {
    reset(['pipelineTemplatesStore.selectedTemplate']);
  };

  const resetSelectedTemplateFilters = () => {
    reset(['pipelineTemplatesStore.selectedPipelineTemplateFilters']);
  };

  const resetMoreTemplatesLikeThis = () => {
    reset([
      'pipelineTemplatesStore.moreTemplatesLikeThis',
      'pipelineTemplatesStore.moreTemplatesLikeThisStatus',
    ]);
  };

  const resetPipelineTemplates = () => {
    reset([
      'pipelineTemplatesStore.pipelineTemplates',
      'pipelineTemplatesStore.pipelineTemplatesByCategory',
      'pipelineTemplatesStore.pipelineTemplatesStatus',
      'pipelineTemplatesStore.selectedCategory',
      'pipelineTemplatesStore.selectedTemplate',
      'pipelineTemplatesStore.moreTemplatesLikeThis',
      'pipelineTemplatesStore.moreTemplatesLikeThisStatus',
      'pipelineTemplatesStore.pipelineTemplatesSearchValue',
      'pipelineTemplatesStore.pipelineTemplatesFiltersValues',
      'pipelineTemplatesStore.selectedPipelineTemplateFilters',
      'pipelineTemplatesStore.pipelineTemplatesSortValue',
    ]);
  };

  return {
    pipelineTemplates,
    templatesByCategory,
    selectedCategory,
    pipelineTemplatesSearchValue,
    selectedPipelineTemplateFilters,
    pipelineTemplatesSortValue,
    moreTemplatesLikeThis,
    moreTemplatesLikeThisStatus,
    selectedTemplate,
    pipelineTemplatesFiltersValues,
    resetPipelineTemplates,
    getPipelineTemplatesFilterTags,
    getPipelineTemplateByName,
    resetMoreTemplatesLikeThis,
    fetchAllGroupedTemplates,
    getPipelineTemplates,
    getPipelineTemplatesByCategory,
    selectTemplatesCategory,
    selectTemplatesFilters,
    selectTemplatesSortValue,
    resetSelectedTemplateFilters,
    resetSelectedTemplate,
    setSelectedPipelineTemplate,
    getMoreLikeThisPipelineTemplates,
    setTemplatesSearchValue,
  };
};

export default usePipelineTemplates;
